import { useState } from 'react';

const HomePage = () => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    site_web: '',
    budget: '',
    objectifs: '',
    services: [] as string[]
  });

  const services = [
    {
      icon: 'ri-search-line',
      title: 'Audit SEO Complet',
      description:
        "Analyse technique approfondie de votre site web pour identifier tous les points d'amélioration SEO."
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Optimisation Technique',
      description:
        "Amélioration de la vitesse, structure, balisage et tous les aspects techniques pour le référencement."
    },
    {
      icon: 'ri-article-line',
      title: 'Stratégie de Contenu',
      description:
        "Création et optimisation de contenu de qualité pour améliorer votre positionnement sur Google."
    },
    {
      icon: 'ri-links-line',
      title: 'Netlinking Qualité',
      description:
        "Acquisition de liens de qualité pour renforcer l'autorité et la crédibilité de votre site."
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Suivi & Reporting',
      description:
        "Monitoring continu des performances et rapports détaillés sur l'évolution de votre référencement."
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Formation SEO',
      description:
        "Formation personnalisée pour votre équipe afin de maintenir et améliorer votre SEO en interne."
    }
  ];

  const testimonials = [
    {
      name: 'Marie Dubois',
      company: 'E-commerce Mode',
      text:
        "Grâce à leur expertise SEO, notre trafic organique a augmenté de 250% en 6 mois. Un investissement très rentable !",
      rating: 5
    },
    {
      name: 'Pierre Martin',
      company: "Cabinet d'Avocat",
      text:
        "Excellent travail sur notre référencement local. Nous sommes maintenant en première page pour tous nos mots-clés cibles.",
      rating: 5
    },
    {
      name: 'Sophie Laurent',
      company: 'Restaurant Gastronomique',
      text:
        "L'équipe est très professionnelle et les résultats sont au rendez-vous. Notre visibilité en ligne s'est considérablement améliorée.",
      rating: 5
    }
  ];

  const plans = [
    {
      name: 'Audit SEO',
      price: '497€',
      description: 'Analyse complète de votre site',
      features: [
        'Audit technique complet',
        'Analyse de la concurrence',
        'Recherche de mots-clés',
        "Plan d'action détaillé",
        'Rapport de 50+ pages'
      ]
    },
    {
      name: 'Optimisation Standard',
      price: '1 497€/mois',
      description: 'Optimisation continue de votre SEO',
      features: [
        "Tout de l'audit SEO",
        'Optimisation technique',
        'Création de contenu (4 articles/mois)',
        'Netlinking (5 liens/mois)',
        'Suivi mensuel des positions'
      ],
      popular: true
    },
    {
      name: 'Stratégie Premium',
      price: '2 997€/mois',
      description: 'Solution complète pour dominer Google',
      features: [
        "Tout de l'optimisation standard",
        'Création de contenu (8 articles/mois)',
        'Netlinking premium (15 liens/mois)',
        'Optimisation technique avancée',
        'Support prioritaire',
        'Formation équipe incluse'
      ]
    }
  ];

  const faqs = [
    {
      question: 'Combien de temps faut-il pour voir les premiers résultats SEO ?',
      answer:
        'Les premiers résultats SEO apparaissent généralement entre 3 à 6 mois. Cependant, certaines améliorations techniques peuvent avoir un impact plus rapide, dès les premières semaines.'
    },
    {
      question: 'Garantissez-vous une position en première page de Google ?',
      answer:
        "Nous ne pouvons pas garantir une position spécifique car Google contrôle ses algorithmes. Cependant, nous nous engageons à améliorer significativement votre visibilité et votre trafic organique."
    },
    {
      question: 'Quelle est la différence entre SEO et SEA ?',
      answer:
        "Le SEO (référencement naturel) vise à améliorer votre positionnement organique sur le long terme, tandis que le SEA (référencement payant) génère du trafic immédiat via des annonces payantes."
    },
    {
      question: 'Travaillez-vous avec tous les types de sites web ?',
      answer:
        "Oui, nous travaillons avec tous types de sites : e-commerce, sites vitrine, blogs, sites institutionnels, etc. Chaque stratégie est adaptée à votre secteur d'activité."
    },
    {
      question: 'Puis-je arrêter le service à tout moment ?',
      answer:
        'Oui, nos contrats sont sans engagement. Vous pouvez arrêter le service avec un préavis de 30 jours. Nous recommandons cependant un minimum de 6 mois pour des résultats optimaux.'
    },
    {
      question: 'Que comprend exactement un audit SEO ?',
      answer:
        "Notre audit SEO comprend l'analyse technique, l'audit de contenu, l'analyse de la concurrence, la recherche de mots-clés, l'audit des backlinks et un plan d'action détaillé avec priorités."
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleMultiStepSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'services') {
        submitData.append(key, (value as string[]).join(', '));
      } else {
        submitData.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d48b08jhnd8fvojd2su0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(submitData as any).toString()
      });

      if (response.ok) {
        alert(
          'Votre demande de devis a été envoyée avec succès ! Nous vous recontacterons dans les 24h.'
        );
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          entreprise: '',
          site_web: '',
          budget: '',
          objectifs: '',
          services: []
        });
        setCurrentStep(1);
      } else {
        const errorText = await response.text();
        console.error('Form submission error:', errorText);
        alert("Une erreur est survenue. Veuillez réessayer.");
      }
    } catch (error) {
      console.error('Form submission exception:', error);
      alert("Une erreur est survenue. Veuillez réessayer.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1
                className="text-2xl font-bold text-gray-900"
                style={{ fontFamily: '"Roboto", sans-serif' }}
              >
                Devis SEO
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                Services
              </a>
              <a href="#tarifs" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                Tarifs
              </a>
              <a href="#temoignages" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                Témoignages
              </a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                FAQ
              </a>
            </nav>
            <a
              href="#devis"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Devis Gratuit
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section with Multi-Step Form */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage:
              "url('https://public.readdy.ai/ai/img_res/584f009f92c0824cb2b536abb7612c28.jpg')"
          }}
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div className="text-left">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Boostez votre <span className="text-blue-600">visibilité</span> sur Google
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed font-medium">
                Augmentez votre trafic organique et générez plus de leads qualifiés grâce à notre
                expertise SEO. Résultats mesurables garantis en 6 mois maximum.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">250%</div>
                  <div className="text-sm text-gray-700 font-medium">Augmentation moyenne du trafic</div>
                </div>
                <div className="text-center bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">150+</div>
                  <div className="text-sm text-gray-700 font-medium">Clients satisfaits</div>
                </div>
                <div className="text-center bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">95%</div>
                  <div className="text-sm text-gray-700 font-medium">Taux de satisfaction</div>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-700 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  <span className="font-medium">Audit gratuit inclus</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  <span className="font-medium">Réponse sous 24h</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  <span className="font-medium">Sans engagement</span>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Obtenez votre Devis Gratuit
                </h3>
                <p className="text-gray-600">
                  Recevez une analyse personnalisée sous 24h
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Étape {currentStep} sur 3</span>
                  <span className="text-sm text-gray-500">{Math.round((currentStep / 3) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                  ></div>
                </div>
              </div>

              <form onSubmit={handleMultiStepSubmit} data-readdy-form id="devis-seo-hero">
                {/* Step 1: Contact Info */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Vos informations</h4>
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        required
                        value={formData.nom}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        placeholder="Votre nom et prénom"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email professionnel *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={nextStep}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Continuer
                    </button>
                  </div>
                )}

                {/* Step 2: Business Info */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Votre entreprise</h4>
                    <div>
                      <label htmlFor="entreprise" className="block text-sm font-medium text-gray-700 mb-2">
                        Entreprise *
                      </label>
                      <input
                        type="text"
                        id="entreprise"
                        name="entreprise"
                        required
                        value={formData.entreprise}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>
                    <div>
                      <label htmlFor="site_web" className="block text-sm font-medium text-gray-700 mb-2">
                        Site web actuel *
                      </label>
                      <input
                        type="url"
                        id="site_web"
                        name="site_web"
                        required
                        value={formData.site_web}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        placeholder="https://votre-site.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                        Budget mensuel envisagé
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Sélectionnez votre budget</option>
                        <option value="500-1000">500€ - 1 000€</option>
                        <option value="1000-2000">1 000€ - 2 000€</option>
                        <option value="2000-5000">2 000€ - 5 000€</option>
                        <option value="5000+">Plus de 5 000€</option>
                      </select>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="flex-1 border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Retour
                      </button>
                      <button
                        type="button"
                        onClick={nextStep}
                        className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Continuer
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Project Details */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Votre projet</h4>
                    <div>
                      <label htmlFor="objectifs" className="block text-sm font-medium text-gray-700 mb-2">
                        Vos objectifs SEO *
                      </label>
                      <textarea
                        id="objectifs"
                        name="objectifs"
                        required
                        rows={3}
                        maxLength={500}
                        value={formData.objectifs}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm resize-none"
                        placeholder="Décrivez vos objectifs..."
                      ></textarea>
                      <div className="text-xs text-gray-500 mt-1">Maximum 500 caractères</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Services qui vous intéressent
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          'Audit SEO',
                          'Optimisation technique',
                          'Création de contenu',
                          'Netlinking',
                          'Suivi des positions',
                          'Formation SEO'
                        ].map(service => (
                          <label key={service} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.services.includes(service)}
                              onChange={() => handleCheckboxChange(service)}
                              className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            />
                            <span className="text-sm text-gray-700">{service}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="flex-1 border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Retour
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Recevoir mon Devis
                      </button>
                    </div>
                  </div>
                )}

                {selectedPlan && (
                  <input type="hidden" name="formule" value={selectedPlan} />
                )}
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                En soumettant ce formulaire, vous acceptez d'être recontacté par notre équipe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Nos Services SEO</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une approche complète et personnalisée pour améliorer votre référencement naturel et dominer votre
              marché.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl text-blue-600`}></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h4>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Tarifs Transparents</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choisissez la formule qui correspond à vos besoins et à votre budget. Tous nos tarifs sont sans
              engagement.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-xl shadow-lg relative ${
                  plan.popular ? 'ring-2 ring-blue-600' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Plus Populaire
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                  <div className="text-4xl font-bold text-blue-600 mb-2">{plan.price}</div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelectedPlan(plan.name)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Choisir cette formule
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="temoignages" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ce que disent nos clients
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez les témoignages de nos clients qui ont transformé leur visibilité en ligne grâce à
              notre expertise SEO.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Questions Fréquentes
            </h3>
            <p className="text-xl text-gray-600">
              Trouvez les réponses aux questions les plus courantes sur nos services SEO.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-gray-500 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Prêt à dominer Google ?
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Rejoignez plus de 150 entreprises qui nous font confiance pour leur référencement naturel.
            Commencez dès aujourd'hui avec un audit gratuit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#devis"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Audit Gratuit
            </a>
            <button
              onClick={() =>
                document.querySelector('#vapi-widget-floating-button')?.click()
              }
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Parler à un Expert
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4
                className="text-lg font-semibold text-gray-900 mb-4"
                style={{ fontFamily: '"Roboto", sans-serif' }}
              >
                Expert SEO
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Votre partenaire de confiance pour améliorer votre visibilité sur Google et
                générer plus de trafic qualifié.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Services</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#services" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Audit SEO
                  </a>
                </li>
                <li>
                  <a href="#services" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Optimisation technique
                  </a>
                </li>
                <li>
                  <a href="#services" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Création de contenu
                  </a>
                </li>
                <li>
                  <a href="#services" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Netlinking
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Ressources</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#faq" className="hover:text-blue-600 transition-colors cursor-pointer">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#temoignages" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Témoignages
                  </a>
                </li>
                <li>
                  <a href="#tarifs" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Tarifs
                  </a>
                </li>
                <li>
                  <a href="#devis" className="hover:text-blue-600 transition-colors cursor-pointer">
                    Devis gratuit
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Contact</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  contact@expert-seo.fr
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  01 23 45 67 89
                </li>
                <li className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  Lun-Ven 9h-18h
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-600">
            <p>
              © 2024 Expert SEO. Tous droits réservés. |
              <a
                href="https://readdy.ai/?origin=logo"
                className="text-blue-600 hover:text-blue-700 ml-1 cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
